import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Skeleton from "./Skeleton";
import { useLanguage } from "../../context/LanguageContext";
import { pickLocalizedName } from "../../utils/localizedName";
import "./clientTheme.css";
import "./CategoryChips.css";

function ArrowLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

export default function CategoryChips({ title, categories, loading }) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  function updateScrollState() {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  }, [categories]);

  function scrollRow(direction) {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.7, behavior: "smooth" });
  }

  if (loading) {
    return (
      <section className="category-chips-section">
        {title && (
          <div className="category-chips-header">
            <h2 className="category-chips-title">{title}</h2>
          </div>
        )}
        <div className="category-chips-scroll">
          <div className="category-chips">
            {Array.from({ length: 15 }).map((_, index) => (
              <div className="category-chip" key={index}>
                <Skeleton className="category-chip-skeleton-image" />
                <Skeleton className="category-chip-skeleton-label" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!categories || categories.length === 0) return null;

  return (
    <section className="category-chips-section">
      {title && (
        <div className="category-chips-header">
          <h2 className="category-chips-title">{title}</h2>
        </div>
      )}
      <div className="category-chips-row">
        <div className="category-chips-scroll" ref={scrollRef} onScroll={updateScrollState}>
          <div className="category-chips">
            {categories.map((category) => {
              const name = pickLocalizedName(language, category.name, category.nameArabic, category.nameHebrew);
              return (
                <button
                  key={category.categoryId}
                  type="button"
                  className="category-chip"
                  onClick={() => navigate(`/products?categoryId=${category.categoryId}`)}
                >
                  {category.imageUrl ? (
                    <img src={category.imageUrl} alt="" className="category-chip-image" />
                  ) : (
                    <div className="category-chip-image category-chip-image-placeholder">
                      {name.charAt(0)}
                    </div>
                  )}
                  <span className="category-chip-label">{name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {canScrollLeft && (
          <button
            type="button"
            className="category-chips-arrow category-chips-arrow-left"
            onClick={() => scrollRow(-1)}
            aria-label="Scroll categories left"
          >
            <ArrowLeftIcon />
          </button>
        )}
        {canScrollRight && (
          <button
            type="button"
            className="category-chips-arrow category-chips-arrow-right"
            onClick={() => scrollRow(1)}
            aria-label="Scroll categories right"
          >
            <ArrowRightIcon />
          </button>
        )}
      </div>
    </section>
  );
}
